/**
 * One chat on the WhatsApp review page (sketch §3, "Entry row"): who it is,
 * the newest message, how many messages it holds, and, in its last 7 days
 * before the 30-day deletion, when it is deleted. A selected row shows what
 * it is saved as: an optional name when the phone has none saved for the
 * number, contact type, active deal with deal type and an optional
 * temperature, and a country code for a number Finishd couldn't split.
 */

import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { hasLetters } from "@/features/realtor/components/contact-initials";
import { ChoiceChips } from "@/features/realtor/components/enquiries/enquiry-shared";
import {
	LEAD_TEMPERATURES,
	LEAD_TYPES,
	type LeadTemperature,
	type LeadType,
} from "@/features/realtor/services/leads-api.service";
import {
	CONTACT_TYPES,
	type ContactType,
} from "@/features/realtor/services/realtor-crm-api.service";
import type { WhatsappReviewEntry } from "@/features/realtor/services/whatsapp-api.service";
import { formatRelativeTime } from "@/features/realtor/utils/relative-time";
import { cn } from "@/lib/utils";

/** What a selected chat is saved as. */
export interface ReviewChoice {
	contactType: ContactType;
	/** Ticked as an active deal: the save also creates a lead. */
	deal: boolean;
	leadType: LeadType;
	/** Optional: left out of the save when unset. */
	temperature: LeadTemperature | undefined;
	/** Digits only; sent only for a chat whose number didn't split. */
	countryCode: string;
	/**
	 * The contact's name, typed on a chat with no name saved on the phone.
	 * Sent trimmed when not blank.
	 */
	name: string;
}

export type CountryCodeError = "required" | "invalid";

/** The backend deletes a chat this long after its later of newest message and arrival. */
export const REVIEW_RETENTION_DAYS = 30;
/** A chat this close to deletion shows when it is deleted. */
export const EXPIRY_WARNING_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whole days until the chat is deleted (0 when already due), or null while
 * more than `EXPIRY_WARNING_DAYS` remain. The clock starts at the later of
 * the newest message and the chat's arrival in the review list.
 */
export function daysUntilDeleted(
	entry: Pick<WhatsappReviewEntry, "newestMessageAt" | "enteredAt">,
	now: number = Date.now(),
): number | null {
	const entered = Date.parse(entry.enteredAt);
	const newest = entry.newestMessageAt
		? Date.parse(entry.newestMessageAt)
		: Number.NEGATIVE_INFINITY;
	const left = Math.max(entered, newest) + REVIEW_RETENTION_DAYS * DAY_MS - now;
	if (!Number.isFinite(left) || left > EXPIRY_WARNING_DAYS * DAY_MS) {
		return null;
	}
	return Math.max(0, Math.ceil(left / DAY_MS));
}

/** The lead type a newly ticked deal starts with. */
export function leadTypeFor(contactType: ContactType): LeadType {
	return contactType === "seller" ? "seller" : "buyer";
}

/**
 * What a newly selected chat's Name field starts with: the chat's name when
 * it has letters (a WhatsApp profile name), else empty. Empty for a chat
 * with a name saved on the phone, which shows no Name field.
 */
export function initialName(
	entry: Pick<WhatsappReviewEntry, "name" | "hasSavedName">,
): string {
	return !entry.hasSavedName && hasLetters(entry.name) ? entry.name : "";
}

/** The id of a row's country-code input, for moving focus to it. */
export const countryCodeInputId = (entryId: string): string =>
	`review-country-code-${entryId}`;

export const REVIEW_SELECT_CLASS =
	"h-11 rounded-md border border-dashboard-border bg-dashboard-bg px-3 text-sm text-dashboard-text focus:outline-none focus:ring-2 focus:ring-dashboard-accent/40 md:h-9";

const T = "realtor.whatsappReview";
/** The longest contact name the save accepts. */
const NAME_MAX = 200;

export interface ReviewEntryRowProps {
	entry: WhatsappReviewEntry;
	/** Set while the row is selected. */
	choice: ReviewChoice | undefined;
	/** The server said this chat left the review list. */
	unavailable: boolean;
	countryCodeError: CountryCodeError | undefined;
	/** A save or ignore is running. */
	busy: boolean;
	onToggle: (selected: boolean) => void;
	onChange: (patch: Partial<ReviewChoice>) => void;
}

export function ReviewEntryRow({
	entry,
	choice,
	unavailable,
	countryCodeError,
	busy,
	onToggle,
	onChange,
}: ReviewEntryRowProps): ReactElement {
	const { t, i18n } = useTranslation();
	const selected = choice !== undefined && !unavailable;
	const sortAt = entry.newestMessageAt ?? entry.enteredAt;
	const daysLeft = daysUntilDeleted(entry);

	return (
		<li
			className={cn(
				"px-3 py-3 sm:px-4",
				selected && "bg-dashboard-surface",
				unavailable && "opacity-70",
			)}
		>
			<div className="flex items-start gap-3">
				<label className="-my-2.5 -ml-2.5 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
					<input
						type="checkbox"
						aria-label={t(`${T}.selectEntry`, { name: entry.name })}
						checked={selected}
						disabled={unavailable || busy}
						onChange={(event) => onToggle(event.target.checked)}
						className="h-5 w-5 accent-[var(--color-dashboard-accent)]"
					/>
				</label>
				<div className="min-w-0 flex-1 space-y-0.5">
					<div className="flex items-baseline justify-between gap-3">
						<p className="truncate text-sm font-medium text-dashboard-text">
							{entry.name}
						</p>
						<time
							dateTime={sortAt}
							className="shrink-0 text-xs text-dashboard-muted"
						>
							{formatRelativeTime(
								sortAt,
								i18n.language,
								t("realtor.time.justNow"),
							)}
						</time>
					</div>
					{entry.name !== entry.number && (
						<p className="font-mono text-xs text-dashboard-muted">
							{entry.number}
						</p>
					)}
					<p className="truncate text-sm text-dashboard-muted-alt">
						{entry.latestMessage?.body ?? t(`${T}.noMessages`)}
					</p>
					<p className="text-xs text-dashboard-muted">
						{t(`${T}.messages`, { count: entry.messageCount })}
						{daysLeft !== null && (
							<>
								{" · "}
								<span className="font-medium text-dashboard-pill-awaiting">
									{daysLeft <= 1
										? t(`${T}.deletedWithinDay`)
										: t(`${T}.deletedIn`, { count: daysLeft })}
								</span>
							</>
						)}
					</p>
					{unavailable && (
						<p className="text-xs font-medium text-dashboard-pill-risk">
							{t(`${T}.unavailable`)}
						</p>
					)}
					{selected && (
						<RowControls
							entry={entry}
							choice={choice}
							countryCodeError={countryCodeError}
							busy={busy}
							onChange={onChange}
						/>
					)}
				</div>
			</div>
		</li>
	);
}

function RowControls({
	entry,
	choice,
	countryCodeError,
	busy,
	onChange,
}: {
	entry: WhatsappReviewEntry;
	choice: ReviewChoice;
	countryCodeError: CountryCodeError | undefined;
	busy: boolean;
	onChange: (patch: Partial<ReviewChoice>) => void;
}): ReactElement {
	const { t } = useTranslation();
	const typeId = `review-type-${entry.id}`;
	const nameId = `review-name-${entry.id}`;
	const countryId = countryCodeInputId(entry.id);
	const countryErrorId = `${countryId}-error`;

	return (
		<fieldset
			disabled={busy}
			className="mt-3 space-y-3 rounded-lg border border-dashboard-border bg-dashboard-paper p-3"
		>
			{!entry.hasSavedName && (
				<div className="space-y-1.5">
					<label
						htmlFor={nameId}
						className="block text-xs font-medium text-dashboard-text"
					>
						{t(`${T}.name`)}
					</label>
					<input
						id={nameId}
						type="text"
						autoComplete="off"
						maxLength={NAME_MAX}
						value={choice.name}
						placeholder={t(`${T}.namePlaceholder`)}
						onChange={(event) => onChange({ name: event.target.value })}
						className={cn(REVIEW_SELECT_CLASS, "w-full sm:max-w-xs")}
					/>
				</div>
			)}

			<div className="flex flex-wrap items-end gap-x-4 gap-y-2">
				<div className="space-y-1.5">
					<label
						htmlFor={typeId}
						className="block text-xs font-medium text-dashboard-text"
					>
						{t(`${T}.contactType`)}
					</label>
					<select
						id={typeId}
						value={choice.contactType}
						onChange={(event) =>
							onChange({ contactType: event.target.value as ContactType })
						}
						className={REVIEW_SELECT_CLASS}
					>
						{CONTACT_TYPES.map((type) => (
							<option key={type} value={type}>
								{t(`realtor.messages.contactTypes.${type}`)}
							</option>
						))}
					</select>
				</div>
				<label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium text-dashboard-text md:min-h-9">
					<input
						type="checkbox"
						checked={choice.deal}
						onChange={(event) =>
							onChange(
								event.target.checked
									? { deal: true, leadType: leadTypeFor(choice.contactType) }
									: { deal: false },
							)
						}
						className="h-5 w-5 accent-[var(--color-dashboard-accent)]"
					/>
					{t(`${T}.activeDeal`)}
				</label>
			</div>

			{choice.deal && (
				<div className="space-y-3">
					<ChoiceChips
						name={`review-deal-type-${entry.id}`}
						legend={t(`${T}.dealType`)}
						value={choice.leadType}
						onChange={(leadType) => onChange({ leadType })}
						options={LEAD_TYPES.map((type) => ({
							value: type,
							label: t(`realtor.leads.types.${type}`),
						}))}
						chipClassName="h-11 md:h-8"
					/>
					<ChoiceChips
						name={`review-temperature-${entry.id}`}
						legend={t(`${T}.temperature`)}
						value={choice.temperature}
						onChange={(temperature) => onChange({ temperature })}
						options={LEAD_TEMPERATURES.map((temperature) => ({
							value: temperature,
							label: t(`realtor.leads.temperatures.${temperature}`),
						}))}
						chipClassName="h-11 md:h-8"
					/>
				</div>
			)}

			{entry.phoneCountryCode === null && (
				<div className="space-y-1.5">
					<label
						htmlFor={countryId}
						className="block text-xs font-medium text-dashboard-text"
					>
						{t(`${T}.countryCode`)}
					</label>
					<div className="flex items-center gap-1.5">
						<span className="text-sm text-dashboard-muted" aria-hidden="true">
							+
						</span>
						<input
							id={countryId}
							type="text"
							inputMode="numeric"
							autoComplete="off"
							maxLength={4}
							value={choice.countryCode}
							onChange={(event) =>
								onChange({
									countryCode: event.target.value.replace(/\D/g, ""),
								})
							}
							aria-invalid={countryCodeError ? true : undefined}
							aria-describedby={countryCodeError ? countryErrorId : undefined}
							className={cn(REVIEW_SELECT_CLASS, "w-20")}
						/>
					</div>
					{countryCodeError && (
						<p id={countryErrorId} className="text-xs text-dashboard-pill-risk">
							{countryCodeError === "required"
								? t(`${T}.countryCodeRequired`)
								: t(`${T}.countryCodeInvalid`)}
						</p>
					)}
				</div>
			)}
		</fieldset>
	);
}
